/**
 * tutorSlotChangeOfferAdapter — une offre « Changement de créneau » (`group_attribution.kind =
 * slot_change`) → ce que la carte, la page d'un lien et le choix des horaires en disent.
 *
 * La famille d'un élève du tuteur change l'horaire d'un ou plusieurs cours ; le tuteur garde l'élève en
 * choisissant `pick` horaires parmi ceux que l'offre réserve, un par cours qui change. Le serveur
 * refuse une acceptation qui n'en porte pas exactement `pick` (`StudentSlotChangeOffers::REFUSAL_PICK`).
 *
 * Portage littéral de `tutor/proposals/slotChangeProposal.js` (`isSlotChangeProposal`, `slotChangeOf`,
 * `cardTitle`, `cardInfoLines`, `dialogTitle`, `dialogIntro`, `dialogNote`, `dialogOptions`,
 * `declineConsequence`, `initialSelection` ; la sélection elle-même — `toggleSlot`, `canAccept`,
 * `buildAcceptBody` — vit dans l'état d'interface, `screens/tutor/Availability/tutorOffersUiState`). Les horaires
 * se lisent dans le `label` que le serveur écrit à l'heure du lecteur (« jeudi 18h ») : rien ne les
 * reformate. La charge utile est celle de `StudentSlotChangeOffers::payloadFor`.
 */
import { capitalizeFirst, joinFrench, numberInWords } from "../../../ui/formatters/textFormatter"
import { type UITutorSlotChange, type UITutorSlotChoice } from "../../../ui/types/tutorDispoOffres"
import { WEEKDAY_ORDER } from "./tutorWeekAdapter"

type ApiSlotChangeSlot = {
    live_session_id?: number | null
    /** « Wednesday 17:00 », à l'heure de l'app : seulement pour ranger. */
    day_time?: string | null
    /** « mercredi 17h », à l'heure du lecteur. */
    label?: string | null
    day_note?: string | null
}

/** `StudentSlotChangeOffers::payloadFor`, réduit à ce que le tuteur lit. */
export type ApiSlotChange = {
    pick?: number | string | null
    student?: { firstname?: string | null; class?: string | null } | null
    courses?: { label?: string | null; subjects?: { name?: string | null }[] | null }[] | null
    kept?: { label?: string | null }[] | null
    slots?: ApiSlotChangeSlot[] | null
    /** Les horaires du même changement qu'un autre tuteur a déjà acceptés. */
    taken_slots?: { key?: string | null; label?: string | null }[] | null
    decline_consequence?: string | null
    /** « jeudi 5 novembre » : le jour où ses nouveaux cours commencent. */
    effective_label?: string | null
}

export const SLOT_CHANGE_KIND = "slot_change"

/** `isSlotChangeProposal` + `slotChangeOf` : la charge utile d'une offre de ce type, ou `null`. */
export function slotChangeOf(
    attribution: { kind?: string | null; slot_change?: ApiSlotChange | null } | null | undefined
) {
    return attribution?.kind === SLOT_CHANGE_KIND && attribution.slot_change ? attribution.slot_change : null
}

const pickOf = (offer: ApiSlotChange) => Math.max(0, Number(offer.pick) || 0)
const listOf = <T>(rows: T[] | null | undefined): T[] => (Array.isArray(rows) ? rows : [])
const slotLabel = (slot: { label?: string | null }) => capitalizeFirst(slot.label ?? "")

/** Les noms distincts des matières des cours qui changent, dans leur ordre. */
function subjectNames(offer: ApiSlotChange): string[] {
    const names: string[] = []
    listOf(offer.courses).forEach((course) =>
        listOf(course?.subjects).forEach((subject) => {
            if (subject?.name && !names.includes(subject.name)) names.push(subject.name)
        })
    )
    return names
}

const lowerSubjects = (offer: ApiSlotChange) => joinFrench(subjectNames(offer).map((name) => name.toLowerCase()))

/** `cardTitle` : « Changement de créneau : Léa (4ème) · 2 cours ». */
export function slotChangeCardTitle(offer: ApiSlotChange): string {
    const student = offer.student
    const className = student?.class ? ` (${student.class})` : ""
    const who = student?.firstname ? ` : ${student.firstname}${className}` : ""
    const count = listOf(offer.courses).length
    return `Changement de créneau${who}${count > 1 ? ` · ${count} cours` : ""}`
}

/** `cardInfoLines` : la matière et l'élève, les horaires proposés, le jour où ça commence. */
export function slotChangeCardLines(offer: ApiSlotChange): string[] {
    const pick = pickOf(offer)
    const slots = listOf(offer.slots)
    const slotsLabel = slots.map(slotLabel).join(", ")
    // Un seul créneau à prendre se lit dans la liste ; plusieurs se comptent.
    const choosesSeveral = pick > 1 && slots.length > pick
    const lines = [
        [subjectNames(offer).join(", "), "1 élève", offer.student?.class].filter(Boolean).join(" · "),
        choosesSeveral ? `${pick} créneaux à choisir parmi : ${slotsLabel}` : `Créneaux proposés : ${slotsLabel}`,
    ]
    if (offer.effective_label) lines.push(`Toutes les semaines à partir du ${offer.effective_label}`)
    return lines
}

/** `dialogTitle` : « Garder Léa en maths ? » */
function question(offer: ApiSlotChange): string {
    const firstname = offer.student?.firstname ?? "ton élève"
    const subjects = lowerSubjects(offer)
    return `Garder ${firstname}${subjects ? ` en ${subjects}` : ""} ?`
}

/** `dialogIntro` : quel cours change, combien d'horaires choisir, et dès quand. */
function intro(offer: ApiSlotChange): string {
    const pick = pickOf(offer)
    const courses = listOf(offer.courses)
    const choice = pick === 1 ? "le créneau" : `${pick} créneaux`
    const from = offer.effective_label ? `, chaque semaine dès le ${offer.effective_label}` : ""
    if (courses.length > 1) {
        return `Ses ${numberInWords(courses.length)} cours de ${lowerSubjects(offer)} changent. Choisis ${choice}${from} :`
    }
    return `Sa famille déplace le ${courses[0]?.label ?? ""}. Choisis ${choice}${from} :`
}

/**
 * `dialogNote` : les cours qu'il garde, puis soit que plusieurs créneaux vont ensemble, soit qu'un
 * autre tuteur peut prendre l'offre.
 */
function note(offer: ApiSlotChange): string {
    const pick = pickOf(offer)
    const kept = listOf(offer.kept)
        .map((course) => course?.label)
        .filter((label): label is string => !!label)
    const notes: string[] = []
    if (kept.length > 0) notes.push(`Tu gardes aussi ${kept.length > 1 ? "ses" : "son"} ${joinFrench(kept)}.`)
    notes.push(
        pick > 1
            ? `Les ${numberInWords(pick)} cours vont ensemble.`
            : "Accepte avant qu'un autre tuteur ne commence ce cours."
    )
    return notes.join(" ")
}

/** Le rang d'un horaire qui ne se lit pas : après toute la semaine. */
const UNREADABLE = "9"

/** « Wednesday 17:00 » → « 3 17:00 », pour ranger dans la semaine ; ce qui ne se lit pas passe après. */
function weekOrder(dayTime: string | null | undefined): string {
    const [, day, hour, minute] = /^(\w+)\s+(\d{1,2}):(\d{2})/.exec(String(dayTime ?? "").trim()) ?? []
    const index = WEEKDAY_ORDER.indexOf(day)
    return index < 0 ? UNREADABLE : `${index + 1} ${hour.padStart(2, "0")}:${minute}`
}

/**
 * `dialogOptions` : les horaires à choisir et, parmi eux dans l'ordre de la semaine, ceux qu'un autre
 * tuteur a déjà pris, qui ne se choisissent plus. Un horaire sans séance ne se choisit pas : il tombe.
 */
function choices(offer: ApiSlotChange): UITutorSlotChoice[] {
    type Ranked = { order: string; choice: UITutorSlotChoice }
    const choosable: Ranked[] = listOf(offer.slots)
        .filter((slot) => slot?.live_session_id != null)
        .map((slot) => ({
            order: weekOrder(slot.day_time),
            choice: {
                key: `slot-${slot.live_session_id}`,
                liveSessionId: slot.live_session_id as number,
                label: slotLabel(slot),
                note: slot.day_note || undefined,
                taken: false,
            },
        }))
    const offered = new Set(choosable.map((ranked) => ranked.order))
    const taken: Ranked[] = listOf(offer.taken_slots)
        .filter((slot) => !!slot?.label)
        .map((slot) => ({
            order: weekOrder(slot.key),
            choice: {
                key: `taken-${slot.key ?? slot.label}`,
                liveSessionId: null,
                label: slotLabel(slot),
                note: undefined,
                taken: true,
            },
        }))
        .filter((ranked) => ranked.order === UNREADABLE || !offered.has(ranked.order))
    // Tri stable : à égalité, l'ordre de l'offre.
    return [...choosable, ...taken]
        .sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0))
        .map((ranked) => ranked.choice)
}

/** `initialSelection` : rien à choisir quand l'offre n'a pas plus d'horaires qu'elle n'en demande — tous cochés. */
function initialSelection(offer: ApiSlotChange): number[] {
    const ids = listOf(offer.slots)
        .map((slot) => slot?.live_session_id)
        .filter((id): id is number => id != null)
    return ids.length > 0 && ids.length <= pickOf(offer) ? ids : []
}

/** `declineConsequence` : le repli de la famille dit par le serveur ; à défaut, ce qui vaut toujours. */
function declineConsequence(offer: ApiSlotChange): string {
    return offer.decline_consequence || "Si tu ne peux pas, un autre tuteur prend ce changement."
}

export function toUITutorSlotChange(offer: ApiSlotChange): UITutorSlotChange {
    return {
        pick: pickOf(offer),
        question: question(offer),
        intro: intro(offer),
        choices: choices(offer),
        note: note(offer),
        declineConsequence: declineConsequence(offer),
        initialSelection: initialSelection(offer),
    }
}
